import "./Chart.scss";
import React from "react";
import Plot, { PlotParams } from "react-plotly.js";
import { useResizeDetector } from 'react-resize-detector';
import { connect } from "react-redux";
import { updateParameterValue } from "../../../store/storyline/actions";
import { throttle } from "lodash";
import { PlotMouseEvent, PlotRelayoutEvent } from "plotly.js";
import * as _ from "lodash";
import { RootState } from "../../../store";
import { DocumentedComponent } from "../../../shared/components/DocumentedComponent";
import { useStaticPlot } from "../../../shared/providers/StaticPlotProvider";
import { Popper } from "../../../shared/components";
import clsx from "clsx";
import { useTooltipContainer } from "../../../shared/providers/TooltipContainerProvider";
import { JsxRenderer } from "../ContentRenderer/JsxRenderer";
import { useThemeColors } from "../../../shared/hooks/useThemeColors";
import { getDataProps } from "../../../shared/utilities";

const style = { width: "100%", height: "100%", flexGrow: 1, flexShrink: 1, flexBasis: 1 };
const axesToOverlay = [
    ["xaxis2", "x"],
    ["xaxis3", "x"],
    ["xaxis4", "x"],
    ["xaxis5", "x"],
    ["xaxis6", "x"],
    ["xaxis7", "x"],
    ["xaxis8", "x"],
    ["yaxis2", "y"],
    ["yaxis3", "y"],
    ["yaxis4", "y"],
    ["yaxis5", "y"],
    ["yaxis6", "y"],
    ["yaxis7", "y"],
    ["yaxis8", "y"]
];

export type PlotlyFigure = {
    data: Plotly.Data[];
    layout: Partial<Plotly.Layout>;
};

interface Props extends Omit<PlotParams, "data" | "layout" | "onHover" | "onUnhover" | "onSelected" | "onDeselect"> {
    name?: string;
    input: PlotlyFigure;
    xAxisRangeParameters?: string[];
    updateParameterValue?: typeof updateParameterValue;
    parameterValues?: Map<string, any>;
    navigateTo?: (path: string) => void;
    staticPlot?: boolean;
    disableResizeListener?: boolean;
    getTooltipContent?: (e: PlotMouseEvent) => React.ReactNode;
    tooltipIsInteractive?: boolean;
    hideTooltipArrow?: boolean;
    onHover?: (event: Readonly<Plotly.PlotMouseEvent>, plotData: Partial<Plotly.PlotData>[], updatePlotData: (newData: Plotly.PlotData[]) => void) => boolean;
    onUnhover?: (event: Readonly<Plotly.PlotMouseEvent>, plotData: Partial<Plotly.PlotData>[], updatePlotData: (newData: Plotly.PlotData[]) => void) => void;
    onSelected?: (event: Readonly<Plotly.PlotMouseEvent>, plotData: Partial<Plotly.PlotData>[], updatePlotData: (newData: Plotly.PlotData[]) => void) => void;
    onDeselect?: (plotData: Partial<Plotly.PlotData>[], updatePlotData: (newData: Plotly.PlotData[]) => void) => void;

    /**
     * @deprecated use `getTooltipContent` instead.
     */
    tooltipTemplate?: string;
};

function _Chart(props: Props) {
    const { width: _width, height: _height, ref } = useResizeDetector();
    const { input, xAxisRangeParameters, updateParameterValue, parameterValues, staticPlot: propStaticPlot, disableResizeListener, onClick, onHover, onUnhover, onSelected, onDeselect, navigateTo, tooltipTemplate, getTooltipContent, tooltipIsInteractive, hideTooltipArrow = false, ...rest } = props;
    const customTooltipContent = tooltipTemplate || getTooltipContent;
    const { data: _data, layout: _layout } = input ?? {};
    const [data, setData] = React.useState(_data);
    const [revision, setRevision] = React.useState(0);
    const tooltipTimeoutRef = React.useRef(null);
    React.useEffect(() => {
        const sanitizedData = (_data ?? []).map(d => ({ ...d, hoverinfo: customTooltipContent ? "none" : d.hoverinfo, hovertemplate: customTooltipContent ? null : d.hovertemplate }));
        if (!_.isEqual(sanitizedData, data)) {
            setData(sanitizedData);
        }
    }, [_data]);
    const themeColors = useThemeColors();

    const [layout, setLayout] = React.useState(_layout);
    React.useEffect(() => {
        const sanitizedLayout = {
            ..._layout,
            autosize: true,
            // NB: This cannot be done in CSS, as Plotly uses this for text size calculations...
            font: {
                family: "Helvetica Neue",
                color: themeColors.text
            },
            paper_bgcolor: themeColors.background,
            plot_bgcolor: themeColors.background,
        };

        // Ensure that the any additional axes are overlaying the main axis.  This enables tooltips and click events to work properly for these axes as well...
        axesToOverlay.forEach(([axis, overlaying]) => {
            sanitizedLayout[axis] && !sanitizedLayout[axis].overlaying && !sanitizedLayout[axis].anchor && (sanitizedLayout[axis].overlaying = overlaying);
        });

        // Mutate the click mode to enable selection if the user has provided an onSelected callback...
        if (props.onSelected) {
            sanitizedLayout["clickmode"] = "event+select";
        }

        if (!_.isEqual(sanitizedLayout, layout)) {
            setLayout(sanitizedLayout);
        }
    }, [_layout, themeColors]);

    // If the user is passing in an explicit value for the `staticPlot` prop, use that, otherwise use the canvas-level value...
    const canvasStaticPlot = useStaticPlot();
    const staticPlot = propStaticPlot !== undefined && propStaticPlot !== null ? propStaticPlot : canvasStaticPlot;

    const dataPointClicked = React.useCallback(
        (data) => {
            // Gantt item selected...
            if (xAxisRangeParameters?.length === 2 && data?.points?.[0]?.base && data?.points?.[0]?.value) {
                // Set the xAxisRangeParameter values to the area of the selected item...
                if (parameterValues.get(xAxisRangeParameters[0]) !== data?.points?.[0]?.base || parameterValues.get(xAxisRangeParameters[1]) !== data?.points?.[0]?.value) {
                    updateParameterValue(xAxisRangeParameters[0], data?.points?.[0]?.base);
                    updateParameterValue(xAxisRangeParameters[1], data?.points?.[0]?.value);
                }
            }

            // Generic navigate-on-click handler...
            if (data?.points?.[0]?.pointIndex !== null && data?.points?.[0]?.pointIndex !== undefined) {
                const navigateToValue = data.points[0]?.data?.navigateTo?.[data?.points[0]?.pointIndex];
                if (navigateToValue) {
                    navigateTo?.(navigateToValue);
                }
            }
        },
        [navigateTo, updateParameterValue, xAxisRangeParameters, parameterValues]
    );

    // This memoized, throttled function persists the current axis bounds to parameter values.  This is only really useful 
    // for a date series, where these can be used as datasource parameters in order to highlight the focused data points...
    const persistAxisBoundsToParameters = React.useMemo(
        () =>
            throttle((e: PlotRelayoutEvent) => {
                if (!xAxisRangeParameters || xAxisRangeParameters?.length !== 2) return;

                // Both forms of the autorange value...
                if ((e?.["xaxis.autorange"] || e?.xaxis?.autorange)) {
                    _.forEach(xAxisRangeParameters, p => updateParameterValue(p, undefined));
                }
                // Parameter form 1 for explicit range...
                else if (e?.["xaxis.range[0]"] && e?.["xaxis.range[1]"]) {
                    for (let i = 0; i < xAxisRangeParameters.length; i++) {
                        updateParameterValue(xAxisRangeParameters[i], e[`xaxis.range[${i}]`]);
                    }
                }
                // Parameter form 2 for explicit range...
                else if (e?.["xaxis.range"]?.length === 2) {
                    for (let i = 0; i < xAxisRangeParameters.length; i++) {
                        updateParameterValue(xAxisRangeParameters[i], e["xaxis.range"][i]);
                    }
                }
            }, 1000),
        []);

    if (xAxisRangeParameters && parameterValues.get(xAxisRangeParameters[0]) && parameterValues.get(xAxisRangeParameters[1]) && layout?.xaxis) {
        layout.xaxis.range = [parameterValues.get(xAxisRangeParameters[0]), parameterValues.get(xAxisRangeParameters[1])];
    }

    const [hoverData, setHoverData] = React.useState(null);
    const activeHover = React.useRef(false);

    const handleHover = React.useCallback((e: PlotMouseEvent) => {
        onHover && onHover(e, data, setData) && setRevision(r => r + 1);

        if (!customTooltipContent) {
            return;
        }

        activeHover.current = true;
        // Cancel the last tooltip show timeout, since we've since moved to a new point...
        tooltipTimeoutRef.current && clearTimeout(tooltipTimeoutRef.current);

        tooltipTimeoutRef.current = setTimeout(() => {
            if (activeHover.current) {
                function getBoundingBox() {
                    if (!e.points) {
                        return {
                            x0: e.event.offsetX,
                            x1: e.event.offsetX,
                            y0: e.event.offsetY,
                            y1: e.event.offsetY,
                        };
                    }

                    if (e.points.length === 1) {
                        return e.points[0]["bbox"];
                    }

                    const midpoints = e.points.map(p => ({
                        x: (p["bbox"].x0 + p["bbox"].x1) / 2,
                        y: (p["bbox"].y0 + p["bbox"].y1) / 2,
                        bbox: p["bbox"]
                    }));

                    // Return bounding box of nearest point...
                    return _.chain(midpoints)
                        .sortBy(p => Math.abs(e.event.offsetX - p.x) + Math.abs(e.event.offsetY - p.y))
                        .map(p => p.bbox)
                        .first()
                        .value();
                };

                const offsets = { x: e.event.x - e.event?.["pointerX"], y: e.event.y - e.event?.["pointerY"] };
                const boundingBox = getBoundingBox();
                const getBoundingClientRect = () =>
                    _.isNaN(offsets.x) || _.isNaN(offsets.y) ?
                        (e.event.target as HTMLElement).getBoundingClientRect() :
                        ({
                            width: boundingBox.x1 - boundingBox.x0,
                            height: boundingBox.y1 - boundingBox.y0,
                            left: boundingBox.x0 + offsets.x,
                            right: boundingBox.x1 + offsets.x,
                            top: boundingBox.y0 + offsets.y,
                            bottom: boundingBox.y1 + offsets.y,
                        });

                setHoverData({
                    hoverEvent: e,
                    anchorEl: {
                        getBoundingClientRect,
                        contextElement: e.event.target
                    }
                });
            }
        }, tooltipIsInteractive ? 200 : 0);

    }, [customTooltipContent, onHover]);

    const handleUnhover = React.useCallback((e) => {
        onUnhover && onUnhover(e, data, setData);

        if (!customTooltipContent) {
            return;
        }

        activeHover.current = false;

        setTimeout(() => {
            if (!activeHover.current) {
                setHoverData(null);
            }
        }, tooltipIsInteractive ? 100 : 0);
    }, [customTooltipContent, onUnhover]);

    const handleSelected = React.useCallback((e) => {
        onSelected && onSelected(e, data, setData);
    }, [onSelected]);

    const handleDeselect = React.useCallback(() => {
        onDeselect && onDeselect(data, setData);
    }, [onDeselect]);

    return (
        <div className="fill" style={{ position: "relative" }} ref={ref} {...getDataProps(props)}>
            <Plot
                {...rest}
                data={data}
                layout={layout}
                config={{ displaylogo: false, responsive: true, autosizable: true, staticPlot: staticPlot, displayModeBar: false }}
                style={style}
                onClick={onClick || dataPointClicked}
                onRelayout={persistAxisBoundsToParameters}
                onHover={handleHover}
                onUnhover={handleUnhover}
                onSelected={handleSelected}
                onDeselect={handleDeselect}
                revision={revision}
            />
            {
                customTooltipContent &&
                <ChartTooltip
                    content={customTooltipContent}
                    hoverData={hoverData}
                    onMouseEnter={() => { activeHover.current = true; }}
                    onMouseLeave={handleUnhover}
                    isInteractive={tooltipIsInteractive}
                    arrow={!hideTooltipArrow}
                />
            }
        </div>
    );
}

interface HoverData {
    anchorEl?: any;
    hoverEvent: PlotMouseEvent;
    [key: string]: any;
}

interface ChartTooltipProps {
    content?: string | ((e: PlotMouseEvent) => React.ReactNode);
    hoverData?: HoverData;
    onMouseEnter?: React.MouseEventHandler<HTMLDivElement>;
    onMouseLeave?: React.MouseEventHandler<HTMLDivElement>;
    isInteractive?: boolean;
    arrow?: boolean;
}

function getTooltipContent(content, hoverData) {
    let result;
    if (_.isString(content)) {
        result = content ? <JsxRenderer template={content} data={hoverData} /> : null;
    } else {
        const generatedContent = content(hoverData.hoverEvent);
        result = generatedContent ? generatedContent : null;
    }
    return result;
}

function ChartTooltip(props: ChartTooltipProps) {
    const { content, hoverData, onMouseEnter, onMouseLeave, isInteractive, arrow = true } = props;

    const tooltipContainerRef = useTooltipContainer();
    if (!hoverData) return;

    let tooltipContent = getTooltipContent(content, hoverData);

    return (
        <Popper
            arrow={arrow}
            className={clsx("chart-tooltip", { "non-interactive-element": !isInteractive })}
            open={hoverData != null}
            container={tooltipContainerRef.current}
            anchorEl={hoverData?.anchorEl}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            {tooltipContent}
        </Popper>
    );
}

const Chart = connect(
    (state: RootState) => ({
        parameterValues: state.storyline.parameterValues
    }),
    { updateParameterValue: updateParameterValue as any })(_Chart);

(Chart as DocumentedComponent).metadata = {
    description: "The Chart component is a thin wrapper around the `Plot` component from the [`react-plotly.js`](https://plotly.com/javascript/react/) library.  All additional props are passed directly to the underlying component - please consult the documentation for that component for all the available options.",
    isSelfClosing: true,
    attributes: [
        { name: "input", type: "object", exactType: "PlotlyFigure", description: "The figure to render.  See the [Plotly figure documentation](https://plotly.com/python/figure-structure/) for the structure of this object." },
        { name: "staticPlot", type: "boolean", description: "This optional property indicates whether the plot should be interactive or static.  Defaults to `true` when the plot is rendered on the Minimap/Tooltip and to `false` when rendered on the main canvas. Can be overridden to always be `true` if a static plot is desired." },
        { name: "disableResizeListener", type: "boolean", description: "If the chart is placed within a non-resizable container, this optional flag can be used to improve performance.  Especially useful for table cell renders, where the virtual scrolling behaviour can trip up the default resize handler." },
        { name: "tooltipTemplate", type: "string", description: "The optional custom template to use for chart tooltips.  Can contain any valid JSX.  The underlying `plotly` hover event is exposed as a binding named `hoverEvent`.  `hoverEvent.points[0]` would most likely be used here to access the data for the point." },
        { name: "getTooltipContent", type: "function", exactType: "(hoverEvent: Plotly.PlotMouseEvent) => React.ReactNode", template: `getTooltipContent={(hoverEvent) => $1}`, description: "The optional callback function to use for generating the tooltip content.  This is the non-serializable counterpart to `tooltipTemplate`, which provides the same functionality.  If both `tooltipTemplate` and `getTooltipContent` is provided, `tooltipTemplate` will take precedence." },
        { name: "tooltipIsInteractive", type: "boolean", description: "Optional - defaults to `false`.  If `true`, the custom tooltip will remain open while the user's mouse pointer is within its bounds." },
        { name: "onHover", type: "function", exactType: "(e: Plotly.PlotMouseEvent, plotData: Partial<Plotly.PlotData>[], updatePlotData: (newData: Plotly.PlotData[]) => void) => boolean | void", template: "onHover={(e, plotData, updatePlotData) => {$1}}", description: "If provided, this callback function will be invoked whenever the user hovers over a point in the chart.  The first argument contains the source event (including the points hovered over).  `plotData` and `updatePlotData` can be used to manipulate the plot data directly.  This is useful for adding adornments (outlines, color changes, etc.) to the hovered points or its neighbors." },
        { name: "onUnhover", type: "function", exactType: "(e: Plotly.PlotMouseEvent, plotData: Partial<Plotly.PlotData>[], updatePlotData: (newData: Plotly.PlotData[]) => void) => void", template: "onUnhover={(e, plotData, updatePlotData) => {$1}}", description: "If provided, this callback function will be invoked whenever the user unhovers from a point in the chart.  The first argument contains the source event (including the points previously hovered over).  `plotData` and `updatePlotData` can be used to manipulate the plot data directly.  This is useful for removing adornments added by the `onHover` event." },
        { name: "onSelected", type: "function", exactType: "(e: Plotly.PlotMouseEvent, plotData: Partial<Plotly.PlotData>[], updatePlotData: (newData: Plotly.PlotData[]) => void) => void", template: "onSelected={(e, plotData, updatePlotData) => {$1}}", description: "If provided, this callback function will be invoked whenever the user selects a point in the chart.  The first argument contains the source event (including the points selected).  `plotData` and `updatePlotData` can be used to manipulate the plot data directly.  This is useful for adding adornments (outlines, color changes, etc.) to the selected points or its neighbors." },
        { name: "onDeselect", type: "function", exactType: "(plotData: Partial<Plotly.PlotData>[], updatePlotData: (newData: Plotly.PlotData[]) => void) => void", template: "onDeselect={(plotData, updatePlotData) => {$1}}", description: "If provided, this callback function will be invoked whenever the user deselects a point in the chart.  `plotData` and `updatePlotData` can be used to manipulate the plot data directly.  This is useful for removing adornments added by the `onSelected` event. " },
    ]
};

export default Chart;